import React, { useEffect, useMemo, useState } from 'react';
import './index.css';
import { handleForbiddenResponse } from '../../utils/api';
import { subscribeUser } from '../../push/pushManager';
import { UpdateIcon } from "../shared/UpdateIcon.jsx";

const API_URL = 'https://tkachev-trade.com/api/pumpDump/messages';
const NOTIFICATION_SETTINGS_API = 'https://tkachev-trade.com/api/notification/subscription';
const GENERIC_ERROR = 'Could not load pump/dump messages.';
const TYPE = {
  PUMP: 'PUMP',
  DUMP: 'DUMP',
};
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const STORAGE_KEYS = {
  enabled: 'pumpDumpNotificationsEnabled',
  clientId: 'appClientId'
};

const safeReadFromStorage = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (storageError) {
    console.error('Failed to read from localStorage.', storageError);
    return null;
  }
};

const safeWriteToStorage = (key, value) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch (storageError) {
    console.error('Failed to write to localStorage.', storageError);
  }
};

const sendNotificationSettings = async (authToken, payload) => {
  const response = await fetch(NOTIFICATION_SETTINGS_API, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      Accept: '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (handleForbiddenResponse(response)) {
    throw new Error('Authentication required to update notifications.');
  }

  const parsedPayload = await response.json().catch(() => null);
  if (!response.ok || parsedPayload?.status !== 1) {
    const message = parsedPayload?.errors?.[0] || 'Failed to update notification settings.';
    throw new Error(message);
  }

  return parsedPayload;
};

const checkBrowserSubscriptionStatus = async () => {
  if (typeof window === 'undefined') {
    return { supported: false, permission: 'denied', subscription: null };
  }
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return { supported: false, permission: 'denied', subscription: null };
  }

  const permission = Notification.permission;

  if (permission === 'granted') {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return { supported: true, permission, subscription };
    } catch (subscriptionError) {
      console.error('Failed to inspect browser push subscription.', subscriptionError);
      return { supported: true, permission, subscription: null };
    }
  }

  return { supported: true, permission, subscription: null };
};

const extractSubscription = (subscription) => {
  if (!subscription) {
    return '';
  }
  const subscriptionJson = typeof subscription.toJSON === 'function' ? subscription.toJSON() : null;
  if (
    subscriptionJson &&
    typeof subscriptionJson.endpoint === 'string' &&
    subscriptionJson.endpoint.length > 0 &&
    subscriptionJson.keys &&
    typeof subscriptionJson.keys.p256dh === 'string' &&
    typeof subscriptionJson.keys.auth === 'string'
  ) {
    return subscriptionJson;
  }
  return '';
};

const normalizeType = (value) => (value || '').toUpperCase() === TYPE.DUMP ? TYPE.DUMP : TYPE.PUMP;

const formatPrice = (value) => {
  const numericPrice = Number(value);
  if (!Number.isFinite(numericPrice)) {
    return value ?? '-';
  }
  const minimumFractionDigits = numericPrice >= 1 ? 2 : 4;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits: 8,
  }).format(numericPrice);
};

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '--:--';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const getDateKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const getDateLabel = (date) => {
  const baseLabel = `${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`;
  const now = new Date();
  return date.getFullYear() === now.getFullYear() ? baseLabel : `${baseLabel}, ${date.getFullYear()}`;
};

const buildGroups = (messages, filterType = 'all', sortNewestFirst = true) => {
  if (!Array.isArray(messages)) {
    return [];
  }

  const normalizedItems = messages
    .map((message, index) => {
      const timestamp = Number(message?.timestamp ?? 0);
      if (!Number.isFinite(timestamp)) {
        return null;
      }
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const type = normalizeType(message?.type);
      const pair = typeof message?.pair === 'string' ? message.pair.replace(/\s+/g, ' ').trim() : 'Unknown pair';
      const diff = typeof message?.diff === 'string' ? message.diff.trim() : '';

      return {
        id: `${type}-${timestamp}-${index}`,
        type,
        pair: pair || 'Unknown pair',
        displayPrice: formatPrice(message?.price),
        diff,
        timestamp,
        date,
        timeLabel: formatTime(timestamp),
      };
    })
    .filter(Boolean)
    .filter((item) => {
      if (filterType === 'all') return true;
      return item.type.toLowerCase() === filterType.toLowerCase();
    })
    .sort((a, b) => sortNewestFirst ? b.timestamp - a.timestamp : a.timestamp - b.timestamp);

  const groupsMap = new Map();

  normalizedItems.forEach((item) => {
    const key = getDateKey(item.date);
    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        key,
        label: getDateLabel(item.date),
        items: [],
      });
    }
    groupsMap.get(key).items.push(item);
  });

  return Array.from(groupsMap.values());
};

export const PumpDumpContainer = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notificationActionLoading, setNotificationActionLoading] = useState(false);
  const [notificationActionError, setNotificationActionError] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => safeReadFromStorage(STORAGE_KEYS.enabled) === 'true');
  const [filterType, setFilterType] = useState('all'); // 'all', 'pump', 'dump'
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const resetStoredSubscriptionState = () => {
    safeWriteToStorage(STORAGE_KEYS.enabled, 'false');
    safeWriteToStorage(STORAGE_KEYS.clientId, '');
    setNotificationsEnabled(false);
  };

  useEffect(() => {
    let cancelled = false;

    const syncNotificationState = async () => {
      const storedEnabled = safeReadFromStorage(STORAGE_KEYS.enabled) === 'true';
      if (!storedEnabled) {
        if (!cancelled) {
          setNotificationsEnabled(false);
        }
        return;
      }

      try {
        const { supported, permission, subscription } = await checkBrowserSubscriptionStatus();
        if (cancelled) {
          return;
        }

        const hasValidSubscription = supported && permission === 'granted' && Boolean(subscription);

        if (!hasValidSubscription) {
          if (!cancelled) {
            resetStoredSubscriptionState();
          }
          return;
        }

        setNotificationsEnabled(true);
      } catch (statusError) {
        console.error('Failed to check notification status on load.', statusError);
        if (!cancelled) {
          resetStoredSubscriptionState();
        }
      }
    };

    syncNotificationState();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const token = localStorage.getItem('token');

    if (!token) {
      setMessages([]);
      setError(new Error('Authentication token is missing.'));
      setLoading(false);
      return () => {
        isMounted = false;
        abortController.abort();
      };
    }

    setLoading(true);
    setError(null);

    fetch(API_URL, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: '*/*',
      },
      signal: abortController.signal,
    })
      .then((response) => {
        if (handleForbiddenResponse(response)) {
          return Promise.reject(new Error(GENERIC_ERROR));
        }
        if (!response.ok) {
          throw new Error(GENERIC_ERROR);
        }
        return response.json();
      })
      .then((json) => {
        if (!isMounted) {
          return;
        }
        if (json?.status !== 1 || !Array.isArray(json?.response?.messages)) {
          throw new Error(GENERIC_ERROR);
        }
        setMessages(json.response.messages);
      })
      .catch((fetchError) => {
        if (!isMounted || fetchError.name === 'AbortError') {
          return;
        }
        const normalizedError = fetchError instanceof Error ? fetchError : new Error(GENERIC_ERROR);
        setMessages([]);
        setError(normalizedError);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [reloadKey]);

  const handleToggleNotifications = async () => {
    const authToken = safeReadFromStorage('token');
    if (!authToken) {
      setNotificationActionError('Authentication token is missing.');
      return;
    }
    const desiredState = !notificationsEnabled;
    let storedClientId = safeReadFromStorage(STORAGE_KEYS.clientId) || '';
    setNotificationActionLoading(true);
    setNotificationActionError(null);
    try {
      const { supported, permission, subscription } = await checkBrowserSubscriptionStatus();

      if (!supported) {
        resetStoredSubscriptionState();
        setNotificationActionError('Нотификации не поддерживаются в вашем браузере.');
        return;
      }

      if (desiredState && permission === 'denied') {
        resetStoredSubscriptionState();
        setNotificationActionError('Нотификации заблокированы. Включите их в браузере.');
        return;
      }

      const subscriptionRevoked = permission !== 'granted' || !subscription;

      if (subscriptionRevoked) {
        storedClientId = '';
        resetStoredSubscriptionState();
      }

      if (storedClientId) {
        await sendNotificationSettings(authToken, {
          enabled: desiredState,
          clientId: storedClientId,
        });
      } else if (desiredState){
        let activeSubscription = subscription;
        if (!activeSubscription) {
          activeSubscription = await subscribeUser();
          if (!activeSubscription) {
            setNotificationActionError('Разрешение на нотификации было отклонено');
            return;
          }
        }
        const subscriptionJson = extractSubscription(activeSubscription);
        if (!subscriptionJson) {
          throw new Error('Ошибка в получении подписки');
        }
        const payload = await sendNotificationSettings(authToken, {
          enabled: desiredState,
          endpoint: subscriptionJson.endpoint,
          publicKey: subscriptionJson.keys.p256dh,
          auth: subscriptionJson.keys.auth,
        });
        const newClientId = payload?.response?.id;
        if (newClientId === null || newClientId === undefined) {
          throw new Error('Invalid subscription identifier received from server.');
        }
        const clientIdValue = String(newClientId);
        if (!clientIdValue) {
          throw new Error('Invalid subscription identifier received from server.');
        }
        safeWriteToStorage(STORAGE_KEYS.clientId, clientIdValue);
      }
      safeWriteToStorage(STORAGE_KEYS.enabled, String(desiredState));
      setNotificationsEnabled(desiredState);
    } catch (actionError) {
      const message = actionError instanceof Error ? actionError.message : 'Failed to update notification settings.';
      setNotificationActionError(message);
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const handleRefresh = () => {
    setReloadKey((prev) => prev + 1);
  };

  const handleFilterClick = (type) => {
    if (filterType === type) {
      setFilterType('all');
    } else {
      setFilterType(type);
    }
  };


  const groups = useMemo(() => buildGroups(messages, filterType, sortNewestFirst), [messages, filterType, sortNewestFirst]);
  const showEmptyState = !loading && !error && groups.length === 0;

  return (
    <div className="pumpdump-container">


      <div className="pumpdump-filters">
        <button
          type="button"
          className={`pumpdump-filter-btn ${filterType === 'dump' ? 'pumpdump-filter-btn--active' : ''}`}
          onClick={() => handleFilterClick('dump')}
        >
          <div
            className="pumpdump-filter-btn__circle pumpdump-filter-btn__circle--red"></div>
          <span>DUMP</span>
        </button>
        <button
          type="button"
          className={`pumpdump-filter-btn ${filterType === 'pump' ? 'pumpdump-filter-btn--active' : ''}`}
          onClick={() => handleFilterClick('pump')}
        >
          <div
            className="pumpdump-filter-btn__circle pumpdump-filter-btn__circle--green"></div>
          <span>PUMP</span>
        </button>
        <button
          type="button"
          className="pumpdump-sort-btn pumpdump-sort-btn--fixed-width"
          onClick={() => setSortNewestFirst(!sortNewestFirst)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M10.45 6.71997L6.72998 3L3.01001 6.71997" stroke="white"
              strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6.72998 21V3" stroke="white" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M13.55 17.2803L17.27 21.0002L20.99 17.2803" stroke="white"
              strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17.27 3V21" stroke="white" strokeLinecap="round"
              strokeLinejoin="round" />
          </svg>
          <span>{sortNewestFirst ? 'Сначала новые' : 'Сначала старые'}</span>
        </button>
        <button
          type="button"
          className="chart-panel__update-button "
          onClick={handleRefresh}
          title={`Update`}
        >
          <UpdateIcon />
        </button>
      </div>

      <div className="pumpdump-content">
        {loading && <div className="pumpdump-status">Загружаем pump/dump...</div>}
        {error && !loading && (
          <div className="pumpdump-status pumpdump-status--error">{error.message}</div>
        )}
        {showEmptyState && <div className="pumpdump-status">No pump/dump messages yet.</div>}
        {groups.map((group) => (
          <div key={group.key} className="pumpdump-group">
            <div className="pumpdump-date">{group.label}</div>
            <div className="pumpdump-list">
              {group.items.map((item) => {
                const isPump = item.type === TYPE.PUMP;
                const badgeLabel = isPump ? 'GREEN' : 'RED';
                const pairParts = item.pair.split('/');
                const coin = pairParts[0]?.trim() || '';
                const quote = pairParts[1] ? ` / ${pairParts[1].trim()}` : '';

                return (
                  <div key={item.id} className={`pumpdump-item pumpdump-item--${item.type.toLowerCase()}`}>
                    <div className='flex flex-col flex-1 gap-1 justify-start'>
                      <span className="pumpdump-item__pair">
                        <span className="pumpdump-item__pair-coin">{coin}</span>
                        <span className="pumpdump-item__pair-quote">{quote}</span>
                      </span>
                      <span className={`pumpdump-item__diff pumpdump-item__diff--${item.type.toLowerCase()}`}>
                        {item.diff}
                      </span>
                    </div>
                    <div className='flex flex-col items-end gap-2 justify-between '>
                      <span className="pumpdump-item__price">${item.displayPrice}</span>
                      <span className="pumpdump-item__time">{item.timeLabel}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="pumpdump-notification-overlay">
        {notificationActionError && (
          <div className="pumpdump-status pumpdump-status--error" style={{ marginTop: 0, marginBottom: 8 }}>
            {notificationActionError}
          </div>
        )}
        <button
          type="button"
          className="pumpdump-notification-btn"
          onClick={handleToggleNotifications}
          disabled={notificationActionLoading}
        >
          {notificationsEnabled ? 'Выключить Уведомления' : 'Включить Уведомления'}
        </button>
      </div>
    </div>
  );
};
